import { type OrderDTO, type OrderLineItemDTO, type SDAccountDataDTO } from "./dto";
import * as ApiHelper from "../../../../api/ApiHelper";
import { format } from "../../../../api/ApiHelper";
import { apiPOST } from "../../../../common/api/apiCallsService";
import { constructUrl, resolveApiUrl, resolveUrl } from "../../../../utils/url/urlResolver";
import { zero } from "../../../../utils/PriceUtils";
import { transformProduct } from "../../../../ProductDetails/transformers/ProductDataTransformer";
import { getWeightObject } from "../../../../utils/WeightUtils";
import { type OrderAccountData, type OrderData, type OrderLineItemData } from "../../../common/modelsV2";
import { groupByFn, isNonNull } from "../../../../utils/reducers";
import { getItemStatus } from "../../../common/GetItemStatus";
import {
  type PaginatedFetchFuncProps,
  type PaginatedFetchFuncResponse,
} from "../../../common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { type GroupedAccountsData } from "../../../common/DownloadOrderHistoryService/types";
import { type OrderDetailsDTO } from "../fetchOrderDetails/dto";
import { type NetPriceData } from "~/models/PriceData";
import { type ProductData } from "~/models/ProductData";
import { isSubscription } from "~/utils/productUtils";
import { isSurchargeIncludedInPriceValue } from "~/utils/features";
import { ALL_CODE } from "~/OrdersQuotes/OrderListPage/components/OrderListSearchBox/useDropdownDataProvider";
import { type QuoteDTO } from "~/OrdersQuotes/QuoteListPage/api/fetchQuoteHistory/dto";
import * as PriceUtils from "~/utils/PriceUtils";
import { transformOrderSubEntries } from "~/OrdersQuotes/OrderListPage/api/fetchOrderDetails/fetchOrderDetails";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface FetchOrderHistoryProps extends PaginatedFetchFuncProps<FetchOrderHistoryRequestData> {}

export interface FetchOrderHistoryRequestData {
  customerOrderId?: string;
  salesDocument?: string;
  lineCustomerOrderId?: string;
  partNumber?: string;
  matnr?: string;
  customerMatnr?: string;
  invoicenr?: string;
  dateFrom?: Date;
  dateTo?: Date;
  sdStatus?: string;
  sdLineStatus?: string;
  account?: GroupedAccountsData;
}

interface OrderHistoryResponseDTO {
  ocsalesDocumentDataList: OrderDTO[];
  nextSkipToken: string | undefined;
  pageCount: number;
  totalCount: number;
}

interface ResponseData {
  results: OrderData[];
  totalResults: number;
  pageCount: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface FetchOrderHistoryResponse extends PaginatedFetchFuncResponse<ResponseData> {}

export async function fetchOrderHistory({
  data,
  skipToken,
  signal,
}: FetchOrderHistoryProps): Promise<FetchOrderHistoryResponse> {
  const { dateFrom, dateTo, ...json } = data;

  if (dateFrom) {
    // @ts-ignore
    json.sdCreationStart = format(dateFrom);
  }

  if (dateTo) {
    // @ts-ignore
    json.sdCreatenEnd = format(dateTo);
  }

  if (json.sdStatus === ALL_CODE) {
    json.sdStatus = "";
  }

  if (json.sdLineStatus === ALL_CODE) {
    json.sdLineStatus = "";
  }

  let url = resolveApiUrl(`/my-account/searchSDList`);
  if (skipToken !== undefined) {
    const params = new URLSearchParams();
    params.set("skiptoken", skipToken);
    url = constructUrl(url, params);
  }

  const response = await apiPOST(url, {
    body: JSON.stringify(json),
    headers: {
      "content-type": "application/json",
      CSRFToken: ApiHelper.getCsrfValue(),
    },
    signal,
  });

  const dto: OrderHistoryResponseDTO = await response.json();

  return {
    data: {
      results: dto.ocsalesDocumentDataList.map(transformOrder),
      totalResults: dto.totalCount,
      pageCount: dto.pageCount,
    },
    nextSkipToken: dto.nextSkipToken,
  };
}

type ProcessableLineItem = Pick<OrderLineItemDTO, "lineNumber" | "scheduleLineNumber">;

/**
 * Process incoming line items.
 *
 * 1. Remove scheduleLineNumber if there's only one line item with a given lineNumber
 *
 * @param items line items of a single order
 */
export function processOrderLineItems<T extends ProcessableLineItem>(items: T[]): void {
  items.sort((a, b) => (a.lineNumber ?? 0) - (b.lineNumber ?? 0));

  const itemsByLineNum = groupByFn(items, (item) => item.lineNumber);
  Object.values(itemsByLineNum).forEach((group) => {
    if (group.length === 1) {
      group[0].scheduleLineNumber = null;
    }
  });
}

export function getLineItemLineNo(lineItem: Pick<OrderLineItemDTO, "lineNumber" | "scheduleLineNumber">) {
  return [lineItem.lineNumber, lineItem.scheduleLineNumber].filter(isNonNull).join(":") ?? "-";
}

export function constructOrderDetailsUrl(
  soNumber: string | undefined | null,
  dto: OrderDTO | OrderDetailsDTO | QuoteDTO,
) {
  let url = "";
  if (soNumber) {
    url = resolveUrl(`/my-account/order?id=${soNumber}`);
    if (isFromDifferentAccOrSalesOrg(dto)) {
      const params = new URLSearchParams();
      params.append("account", dto.account!.code);
      params.append("salesOrg", dto.account!.salesOrg);
      url = url + "&" + params.toString();
    }
  }
  return url;
}

export function transformOrder(dto: OrderDTO): OrderData {
  const products = dto.products ?? [];
  processOrderLineItems(products);

  return {
    detailsUrl: constructOrderDetailsUrl(dto.salesDocument, dto),
    datePlaced: dto.datePlaced ? new Date(dto.datePlaced) : undefined,
    orderStatus: dto.sdStatus || "",
    orderStatusCode: dto.sdStatusCode || "",
    orderType: dto.orderType || "",
    soNumber: dto.salesDocument || "",
    poNumber: dto.customerOrderId || "",
    totalPrice: dto.price ? dto.price : zero(),
    products: dto.products
      ? dto.products
          .map((data) => transformOrderLine(data, false))
          .sort((a, b) => a.lineNumber && b.lineNumber && a.lineNumber - b.lineNumber)
      : [],
    hasSurcharge: dto.hasSurcharge || false,
    account: transformOrderAccount(dto.account),
  };
}

export function transformOrderAccount(data?: SDAccountDataDTO): OrderAccountData | undefined {
  if (!data) {
    return undefined;
  }
  const { endCustomerCodes, ...rest } = data;
  return {
    ...rest,
    endCustomerCode: endCustomerCodes?.[0] ?? "",
  };
}

function getNetPrice(data: OrderLineItemDTO, product: ProductData): NetPriceData {
  const surcharge = data.surcharge ?? 0;
  const baseValue = data.nettoPrice.value;
  return {
    value: isSurchargeIncludedInPriceValue() ? baseValue : baseValue * (1 + surcharge),
    baseValue: baseValue,
    currencyIso: data.nettoPrice.currencyIso,
    unit: +(data.priceUnit ?? "") || 1,
    surcharge,
    isSubscription: isSubscription(product),
    rebate: undefined,
    isSpecial: undefined,
  };
}

export function transformOrderLine(data: OrderLineItemDTO, isQuoteProduct: boolean): OrderLineItemData {
  const product = transformProduct(data.product);
  return {
    product,

    comment: data.comment || "",
    lineNo: getLineItemLineNo(data),
    scheduleLineNumber: data.scheduleLineNumber ?? 0,
    lineNumber: data.lineNumber || 0,

    status: data.lineItemStatus,
    statusCode: data.lineItemStatusCode,

    qty: data.quantityRequested,
    confirmedQty: data.quantityConfirmed,
    shippedQty: data.quantityShipped,

    packCode: data.packCode,
    packSize: data.packSize,
    location: data.primaryWarehouseName,
    deliveryDate: data.deliveryDate ? new Date(data.deliveryDate) : undefined,
    requestedDeliveryDate: data.requestedDeliveryDate ? new Date(data.requestedDeliveryDate) : undefined,

    weight: getWeightObject(data.massPerPiece, data.massUnit),
    netPrice: getNetPrice(data, product),
    priceForTotal: data.priceForTotal || PriceUtils.zero(),
    contractNumber: "",
    contractLine: "",
    priceReference: data.priceReference || "",

    invoiceData: {
      invoiceDate: data.shipmentInformation.invoiceDate || "-",
      invoiceNo: data.shipmentInformation.invoiceNumber || "-",
      invoiceUrl: data.shipmentInformation.invoiceUrl ? resolveUrl(data.shipmentInformation.invoiceUrl) : null,
      trackingNo: data.shipmentInformation.trackingNumber || "-",
      type: data.shipmentInformation.invoiceNumber || "-",
      trackingUrl: data.shipmentInformation.trackingUrl || "-",
      shippingPartnerId: data.shipmentInformation.shippingPartnerID || "-",
      shippingPartnerName: data.shipmentInformation.shippingPartnerName || "-",
      deliveryNoteNumber: data.shipmentInformation.deliveryNoteNumber ?? "-",
      deliveryNoteUrl: data.shipmentInformation.deliveryNoteUrl ?? "",
    },
    quoteNumber: data.quoteNumber || "",
    notaFiscal:
      data.shipmentInformation && data.shipmentInformation.notaFiscal ? data.shipmentInformation.notaFiscal : null,
    quoteLineItemStatus: isQuoteProduct ? getItemStatus(data.lineItemStatusCode) : undefined,
    customerOrderNumber: data.customerOrderNumber ?? "",
    orderable: data.orderable,
    logisticalComment: data.logisticalComment,
    subEntries: data.subEntries?.map((item) => transformOrderSubEntries(item, product)) ?? [],
    mainQuotes: [],
    statusEnum: getItemStatus(data.lineItemStatusCode),
  };
}

export const isFromDifferentAccOrSalesOrg = (row: OrderDTO | OrderDetailsDTO | QuoteDTO) => {
  return (
    (row.account?.code && row.account?.code !== window.SCHA.user.selectedAccount.uid) ||
    (row.account?.salesOrg && row.account?.salesOrg !== window.SCHA.user.selectedAccount.salesOrg)
  );
};
